import type { GameType } from './card'
import type { IInventoryItem } from './inventory'
import type { IAddress } from './user'
import type { Currency } from './wallet'

/**
 * Mirrors the backend `Withdrawal` schema.
 *
 * The legacy admin's copy of this type was stale: it was missing `pending_payment`,
 * `packed`, and `returned`, along with the tracking and source fields. That is not
 * cosmetic -- a board built on the old enum silently drops every order sitting in a
 * status it does not know about.
 */
export enum WithdrawalStatus {
    /**
     * Shipping fee still being paid through Stripe. No stock is reserved and
     * fulfillment cannot see it; only the Stripe webhook moves it to `created`.
     */
    PENDING_PAYMENT = 'pending_payment',
    CREATED = 'created',
    ORDERED = 'ordered',
    PENDING = 'pending',
    PACKED = 'packed',
    SHIPPING = 'shipping',
    COMPLETED = 'completed',
    /** Carrier returned the parcel. Terminal, and deliberately not `completed`. */
    RETURNED = 'returned',
    CANCELLED = 'cancelled',
    REFUNDED = 'refunded'
}

export enum WithdrawalSource {
    /** Shipping items the user already owned. */
    BACKPACK = 'backpack',
    /** Bought from the catalogue and shipped directly. */
    MARKET = 'market'
}

/**
 * What `/withdrawal/update` will accept, copied from the backend's
 * `ADMIN_ALLOWED_STATUS_TRANSITIONS`. Everything else needs a dedicated endpoint,
 * so the board only offers moves that can actually succeed rather than letting an
 * operator discover the rule through a 400.
 */
export const ALLOWED_STATUS_TRANSITIONS: Record<WithdrawalStatus, WithdrawalStatus[]> = {
    [WithdrawalStatus.PENDING_PAYMENT]: [],
    [WithdrawalStatus.CREATED]: [WithdrawalStatus.ORDERED, WithdrawalStatus.PENDING],
    [WithdrawalStatus.ORDERED]: [WithdrawalStatus.PENDING, WithdrawalStatus.SHIPPING],
    [WithdrawalStatus.PENDING]: [WithdrawalStatus.ORDERED, WithdrawalStatus.SHIPPING],
    [WithdrawalStatus.PACKED]: [],
    [WithdrawalStatus.SHIPPING]: [WithdrawalStatus.COMPLETED, WithdrawalStatus.RETURNED],
    [WithdrawalStatus.COMPLETED]: [],
    [WithdrawalStatus.RETURNED]: [],
    [WithdrawalStatus.CANCELLED]: [],
    [WithdrawalStatus.REFUNDED]: []
}

export function canTransition(from: WithdrawalStatus, to: WithdrawalStatus): boolean {
    if (from === to) return true
    return ALLOWED_STATUS_TRANSITIONS[from]?.includes(to) ?? false
}

export interface IWithdrawalNote {
    _id: string
    author: string
    note: string
    createdAt: Date
}

export interface IWithdrawal {
    _id: string

    /** Populated with `_id details.name details.dname details.photo`. */
    user: {
        _id: string
        details?: {
            name?: string
            dname?: string
            photo?: string
        }
    }

    email?: string

    status: WithdrawalStatus
    source?: WithdrawalSource
    isPriority?: boolean

    shipping: {
        info: string
        delivery: Date
        reference: string
        address: IAddress
        tokensUsed?: number
        feePaid?: number
        /** Shipping fee taken through Stripe, in cents. */
        stripeAmount?: number
        stripeSessionId?: string
        stripeCheckoutUrl?: string
    }

    items: IInventoryItem[]
    /** Every game present in the order. Single-entry orders are the filterable ones. */
    games?: GameType[]
    notes?: IWithdrawalNote[]

    trackingNumber?: string
    carrierName?: string
    shipstationShipmentId?: string
    shipstationLabelUrl?: string

    labelLockedAt?: Date

    amount: number
    currency: Currency

    createdAt: Date
    updatedAt: Date
}
